<template>
  <div>
    <el-dialog v-dialogDrag :visible.sync="dialogVisible"
               class="translate-dialog"
               :modal="false">
      <div slot="title"
           class="dialog-header">
        <div class="translate-icon">译</div>
        <div class="collect-title">白话文翻译</div>
        <div class="line-area"></div>
      </div>
      <div class="translation-body"
           v-loading="loading">
        <div class="source-label">{{ sourceLabel }}</div>
        <div class="section-title">原文</div>
        <div class="original-text">{{ originalText || originalPlaceholder }}</div>
        <div class="section-title">白话文</div>
        <div ref="content"
             class="translated-text"
             :class="{ error: errorMessage }">
          <span v-if="errorMessage">{{ errorMessage }}</span>
          <span v-else-if="translatedText">{{ translatedText }}</span>
          <span v-else>{{ loading ? '正在翻译中...' : '暂无翻译结果' }}</span>
        </div>
        <div v-if="metaText"
             class="meta-text">{{ metaText }}</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  buildTranslatePayload,
  getTranslateErrorMessage,
  parseTranslateResponse
} from '@/utils/aiTranslate.js'

export default {
  name: 'RetrieveDialogTranslate',
  props: {
    openTranslate: {
      type: Boolean,
      required: true
    },
    bookId: {
      type: String,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      originalText: '',
      translatedText: '',
      errorMessage: '',
      sourceLabel: '当前页文字',
      loading: false,
      meta: null
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.openTranslate
      },
      set (value) {
        if (!value) {
          this.$emit('closeTranslate', false)
        }
      }
    },
    originalPlaceholder () {
      return this.loading ? '正在读取待翻译文本...' : '暂无原文'
    },
    metaText () {
      if (!this.meta) return ''
      var parts = []
      if (this.meta.model) {
        parts.push(this.meta.model)
      }
      if (this.meta.totalTokens) {
        parts.push('tokens ' + this.meta.totalTokens)
      }
      if (this.meta.latencyMs) {
        parts.push((this.meta.latencyMs / 1000).toFixed(1) + 's')
      }
      return parts.join(' · ')
    }
  },
  created () {
    this.translateCurrentText()
  },
  methods: {
    getSelectedText () {
      if (typeof window === 'undefined' || !window.getSelection) return ''
      return window.getSelection().toString().trim()
    },
    getPageText () {
      let params = new URLSearchParams()
      params.append('bookId', this.bookId)
      params.append('page', this.currentPage)
      return this.$http.post('retrieve/page/getText', params, { timeout: 30000 }).then((res) => {
        if (!res || !res.data || res.data.status !== 200) {
          throw new Error((res && res.data && res.data.message) || '当前页文字读取失败')
        }
        var info = res.data.data || {}
        var text = info.textAll || info.text || ''
        text = String(text).trim()
        if (!text) {
          throw new Error('本页暂无可翻译文本')
        }
        return text
      })
    },
    translateCurrentText () {
      this.loading = true
      this.originalText = ''
      this.translatedText = ''
      this.errorMessage = ''
      this.meta = null

      var selectedText = this.getSelectedText()
      var sourceType = selectedText.length >= 2 ? 'selection' : 'paragraph'
      this.sourceLabel = sourceType === 'selection' ? '选中文本' : '当前页文字'
      var textPromise = sourceType === 'selection'
        ? Promise.resolve(selectedText)
        : this.getPageText()

      textPromise.then((text) => {
        var storage = typeof window !== 'undefined' ? window.localStorage : null
        var payload = buildTranslatePayload({
          text: text,
          sourceType: sourceType,
          bookId: this.bookId
        }, storage)
        this.originalText = payload.text
        return this.$http.post('/retrieve/aiChat/translate', payload, { timeout: 90000 })
      }).then((res) => {
        var result = parseTranslateResponse(res)
        this.originalText = result.originalText || this.originalText
        this.translatedText = result.translatedText
        this.meta = {
          model: result.model,
          totalTokens: result.totalTokens,
          latencyMs: result.latencyMs
        }
        this.$nextTick(() => {
          if (this.$refs.content) {
            this.$refs.content.scrollTop = this.$refs.content.scrollHeight
          }
        })
      }).catch((error) => {
        this.errorMessage = getTranslateErrorMessage(error, '翻译失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    }
  },
  watch: {
    currentPage () {
      if (this.openTranslate) {
        this.translateCurrentText()
      }
    }
  }
}
</script>

<style scoped>
* {
  color: #634c37;
  font-size: 16px;
}

::-webkit-scrollbar {
  background-color: #f5eee8;
  width: 4px;
}

::-webkit-scrollbar-thumb {
  background-color: #9e5c1d;
}

.translate-dialog >>> .el-dialog {
  width: 320px !important;
  height: 571px !important;
  box-shadow: 0px 3px 6px 1px rgba(0, 0, 0, 0.16);
  border-radius: 0px 0px 0px 0px;
  opacity: 1;
  border: 1px solid #f7f6f5;
  margin-right: 160px;
  text-align: left;
}

.translate-dialog >>> .el-dialog__header {
  padding: 15px 0 0 16px;
}

.translate-dialog >>> .el-dialog__body {
  padding: 16px 15px 0px 15px;
}

.translate-icon {
  width: 20px;
  height: 20px;
  line-height: 18px;
  border: 1px solid #9e5c1d;
  box-sizing: border-box;
  color: #9e5c1d;
  display: inline-block;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
}

.collect-title {
  font-size: 18px;
  font-family: Source Han Sans CN-Bold, Source Han Sans CN;
  font-weight: bold;
  color: #9e5c1d;
  display: inline;
  margin: 0 0 13px 10px;
  bottom: 13px;
}

.line-area {
  width: 145px;
  margin-top: 12px;
  border-bottom: 1px solid #9e5c1d;
}

.translation-body {
  height: 475px;
  overflow-y: auto;
  padding-right: 4px;
}

.source-label {
  color: #9e5c1d;
  font-size: 13px;
  margin-bottom: 12px;
}

.section-title {
  font-family: Source Han Sans CN-Bold, Source Han Sans CN;
  font-weight: bold;
  color: #634c37;
  margin: 12px 0 7px;
}

.original-text,
.translated-text {
  line-height: 28px;
  white-space: pre-wrap;
  word-break: break-word;
}

.original-text {
  max-height: 132px;
  overflow-y: auto;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0e6dc;
}

.translated-text {
  min-height: 180px;
  color: #3c2e23;
}

.translated-text.error,
.translated-text.error span {
  color: #c0392b;
}

.meta-text {
  margin-top: 12px;
  padding-top: 9px;
  border-top: 1px solid #f0e6dc;
  color: #9b826d;
  font-size: 12px;
}

.translate-dialog >>> .el-dialog__headerbtn {
  top: 5px !important;
  right: 4px;
  background: url('../assets/images/bookIcons/close.png') left no-repeat;
  background-size: cover;
}

.translate-dialog >>> .el-dialog__headerbtn i {
  height: 20px;
  width: 25px;
  visibility: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/RetrieveDialogTranslate.vue